import { useMutation } from '@tanstack/react-query'
import { formatISO } from 'date-fns'
import type { CategoryType, ISO8601Duration, User } from '@opallabs/concepts'
import { computeDurationFromDates } from '@opallabs/lambda'
import { OpalApiResponseDocument } from '../../../schema'
import { blocks } from '../../../v3/blocks'

export type CreateBlockMutationParams = {
  categoryType?: CategoryType | null
  creator: User
  duration?: ISO8601Duration.Representation | null
  endDate: Date | null
  isTemplate?: boolean
  momentId?: string
  planId: string
  row?: number
  startDate: Date | null
  swimlaneId: string | null
  templateDescription?: string | null
  templateName?: string | null
  title: string
  workspaceUuid: string
}

type BlockResponseDocument = OpalApiResponseDocument<'CreateBlockV3', 201>

type QueryParams = {
  onMutate?: (params: CreateBlockMutationParams) => void
  onSuccess?: (
    data: BlockResponseDocument,
    params: CreateBlockMutationParams
  ) => void
}

const buildRequestBody = ({
  categoryType,
  creator,
  duration,
  endDate,
  isTemplate = false,
  momentId,
  planId,
  row = 1,
  startDate,
  swimlaneId,
  templateDescription,
  templateName,
  title,
  workspaceUuid,
}: CreateBlockMutationParams) => ({
  data: {
    type: 'block' as const,
    attributes: {
      start_at: startDate
        ? formatISO(startDate, { representation: 'date' })
        : null,
      duration:
        duration ??
        (startDate && endDate
          ? computeDurationFromDates(startDate, endDate)
          : null),
      title,
      row_index: row,
      y_scale: 1,
      ...(isTemplate
        ? {
            is_template: isTemplate,
            template_name: templateName,
            template_description: templateDescription ?? null,
          }
        : {}),
    },
    relationships: {
      owner: {
        data: { type: 'user' as const, id: creator.uuid },
      },
      workspace: {
        data: { type: 'workspace' as const, id: workspaceUuid },
      },
      view: {
        data: { type: 'view' as const, id: planId },
      },
      category: {
        data: swimlaneId ? { type: 'category' as const, id: swimlaneId } : null,
      },
      ...(momentId
        ? {
            paired_moment: {
              data: { type: 'moment' as const, id: momentId },
            },
          }
        : {}),
      ...(categoryType
        ? {
            category_type: {
              data: { type: 'category_type' as const, id: categoryType.id },
            },
          }
        : {}),
    },
  },
})

export const useCreateBlockMutation = ({
  onMutate,
  onSuccess,
}: QueryParams) => {
  return useMutation({
    mutationFn: (params: CreateBlockMutationParams) =>
      blocks.create({
        requestBody: buildRequestBody(params),
      }),
    onMutate,
    onSuccess,
  })
}
